import React, { useRef, useState } from 'react';
import { uInt8ToBase64 } from '/imports/utils';
import { initiateAndroidUpload, AndroidClient } from '/imports/android';
import type { FileMeta } from '/imports/types';
import { Icon } from '/imports/ui/Icon';
import { finishFileUpload, startFileUpload, uploadFileChunk } from '/imports/api/files';

export interface FilesInputProps {
  className?: string;
  placeholder?: string;
  value?: FileMeta[];
  disabled?: boolean;
  accept?: string;
  onChange?(fileMetas: FileMeta[], e?: React.ChangeEvent<HTMLInputElement>): void;
  label?: string | React.ReactNode;
}

export const FilesInput = (props: FilesInputProps) => {
  const { value } = props;
  const _value = value || [];
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgresses, setUploadProgresses] = useState<number[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length) await handleFiles(newFiles, e);
  };
  // const handleNameChange = (fileMeta: FileMeta, e: React.ChangeEvent<HTMLInputElement>) => {
  //   fileMeta.name = e.target.value;
  //   props.onChange?.([..._value], e);
  // };
  const handleRemoveFile = (index: number) => {
    _value.splice(index, 1);
    props.onChange?.([..._value]);
  };
  const handleFiles = async (newFiles: File[], e?: React.ChangeEvent<HTMLInputElement>) => {
    const fileMetas: FileMeta[] = [];
    const progresses = new Array(newFiles.length).fill(0);
    setFiles([...files, ...newFiles]);
    setUploadProgresses(progresses);
    for (const [i, file] of newFiles.entries()) {
      const { name, type, size } = file;
      const token = await startFileUpload.call({ name, type, size });
      progresses[i] = 1;
      setUploadProgresses([...progresses]);
      const stream = file.stream() as any;
      const reader = stream.getReader();
      let uploadedBytes = 0;
      let read;
      let chunk;
      let fileMeta;
      while ((read = await reader.read())) {
        if (read.value) {
          chunk = uInt8ToBase64(read.value);
          await uploadFileChunk.call({ token, chunk });
          uploadedBytes += read.value.byteLength;
          progresses[i] = Math.round(uploadedBytes / size * 100);
          setUploadProgresses([...progresses]);
        }
        if (read.done) {
          fileMeta = await finishFileUpload.call(token);
          fileMetas[i] = fileMeta;
          progresses[i] = 0;
          setUploadProgresses([...progresses]);
          break;
        }
      }
      fileMetas && props.onChange?.([..._value, ...fileMetas], e);
    }
    if (fileInput.current) fileInput.current.value = '';
  };
  const handleInputClick = (e?: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    if (AndroidClient) initiateAndroidUpload((file: File) => handleFiles([file]));
    else if (!e) fileInput.current?.click();
  };
  return (
    <div className={`files-input bg-panel ${props.className}`}>
      {/* <table>
        <tbody>
          {
            value?.map((fileMeta, i) => {
              return (
                <tr key={i}>
                  <td><Thumb file={fileMeta} /></td>
                  <td>
                    <div className="file-name"><input value={fileMeta.name} onChange={(e) => handleNameChange(fileMeta, e)} /></div>
                    <div className="file-type">{fileMeta.type}</div>
                    <div className="file-size">{formatBytes(fileMeta.size)}</div>
                  </td>
                  <td className="remove-file">
                    <Icon icon="close" onClick={() => handleRemoveFile(i)} />
                  </td>
                </tr>
              );
            })
          }
        </tbody>
      </table> */}
      <div className="thumbnails my-4 flex items-center gap-4">
        {
          value?.map((fileMeta, i) => {
            return (
              <div key={i} className="thumbnail relative">
                <Thumb file={fileMeta} />
                <div className="remove-file absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-2xl bg-primary p-0 [&_svg]:w-3 [&_svg]:cursor-pointer text-on-primary [&:hover_svg]:text-primary">
                  <Icon icon="close" onClick={() => handleRemoveFile(i)} />
                </div>
              </div>
            );
          })
        }
      </div>
      {
        files.length && files.map((file, i) => {
          if (!uploadProgresses[i]) return null;
          return <Progress key={i} progress={uploadProgresses[i]} file={file} />;
        }) || null
      }
      <input
        type="file"
        ref={fileInput}
        className={`box-border hidden w-full min-w-80 rounded-card border border-mid px-3 py-2 font-sans text-base text-fg-1 placeholder:capitalize bg-panel mobile:min-w-0${value ? ' hide' : ''}`}
        placeholder={props.placeholder || ''}
        multiple={true}
        accept={props.accept}
        disabled={props.disabled}
        onClick={(e) => handleInputClick(e)}
        onChange={(e) => handleChange(e)}
      />
      <div
        className={`target rounded-input border-2 border-dashed border-grey-4 p-6 text-center [&.has-files]:px-6 [&.has-files]:py-1 [&_.icon]:m-auto [&_.icon]:flex-row${value?.length ? ' has-files' : ''}`}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onDrop={async (e) => {
          e.preventDefault();
          if (!fileInput.current) return;
          fileInput.current.files = e.dataTransfer.files;
          await handleFiles(Array.from(e.dataTransfer.files));
        }}
      >
        {props.label || (props.accept === 'image/*' ? <Icon icon="addPhotoAlternate" size={40} /> : <Icon icon="postAdd" size={40} />)}
      </div>
    </div>
  );
};

const thumbClass = 'file-widget-thumb inline-flex h-20 w-20 flex-col justify-center border border-mid text-center [&_a]:m-auto [&_a]:flex [&_a]:h-full [&_a]:w-full [&_a]:flex-col [&_a]:justify-center [&_a]:bg-cover [&_a]:bg-center [&_iframe]:max-h-20 [&_iframe]:max-w-20 [&_img]:max-h-20 [&_img]:max-w-20 [&_video]:max-h-20 [&_video]:max-w-20';

interface ThumbProps {
  file: FileMeta
}

const Thumb = (props: ThumbProps) => {
  const file = props.file;
  if (file.type.startsWith('image')) {
    return (
      <div className={thumbClass}>
        <a href={file.url} target='_blank' rel="noreferrer" style={{ backgroundImage: `url('${file.url}')` }} />
      </div>
    );
  }
  if (file.type.startsWith('video')) {
    return (
      <div className={thumbClass}>
        <a href={file.url} target='_blank' rel="noreferrer">
          <video src={file.url} />
        </a>
      </div>
    );
  }
  if (file.type.startsWith('application/pdf')) {
    return (
      <div className={thumbClass}>
        <a href={file.url} target='_blank' rel="noreferrer">
          <iframe src={file.url} />
        </a>
      </div>
    );
  }
  return (
    <div className={thumbClass}>
      <a href={file.url}>{file.name}</a>
    </div>
  );
};

interface ProgressProps {
  progress: number;
  file: File;
}

const Progress = (props: ProgressProps) => {
  const progress = props.progress;
  const file = props.file;
  return (
    <div className='file-widget-progress-wrapper'>
      <div className='file-widget-progress-filename'>{file.name}</div>
      <div className='file-widget-progress-bar mb-4 h-2 w-0 bg-primary transition-[width] duration-200 ease-linear' style={{ width: `${progress}%` }} />
    </div>
  );
};